import { FormControl } from '@angular/forms';
import {
    LabelValue,
    OrganisationUnitsPreferences
} from '@flyfreely-portal-ui/flyfreely';
import moment from 'moment';

/**
 * This helper file houses several of the label and formatting options and functions.
 * These can be used by other components performing the unit conversions, or easily
 * moved to a different location higher up the project tree for more universal access.
 */

/* ==== Formatter values for units preferences ==== */

export const dateFormatOptions: LabelValue[] = [
    { value: 'DD/MM/YYYY', name: 'DD/MM/YYYY' },
    { value: 'MM/DD/YYYY', name: 'MM/DD/YYYY' },
    { value: 'YYYY-MM-DD', name: 'YYYY-MM-DD' },
    { value: 'DD MMM YYYY', name: 'DD Month YYYY' }
];

export const timeFormatOptions: LabelValue[] = [
    { value: 'HH:mm', name: '21:10 (24-hour)' },
    { value: 'hh:mm a', name: '09:10 PM (12-hour, AM/PM)' }
];

export const distanceFormatOptions: LabelValue[] = [
    {
        value: OrganisationUnitsPreferences.DistanceUnit.METRE,
        name: 'Metres (m)'
    },
    {
        value: OrganisationUnitsPreferences.DistanceUnit.MILE,
        name: 'Miles (mi)'
    },
    {
        value: OrganisationUnitsPreferences.DistanceUnit.NAUTICAL_MILE,
        name: 'Nautical Miles (nm)'
    }
];

export const heightFormatOptions: LabelValue[] = [
    {
        value: OrganisationUnitsPreferences.HeightUnit.METRE,
        name: 'Metres (m)'
    },
    { value: OrganisationUnitsPreferences.HeightUnit.FEET, name: 'Feet (ft)' }
];

export const speedFormatOptions: LabelValue[] = [
    {
        value: OrganisationUnitsPreferences.SpeedUnit.METRES_PER_SECOND,
        name: 'Metres per second (m/s)'
    },
    {
        value: OrganisationUnitsPreferences.SpeedUnit.KILOMETRES_PER_HOUR,
        name: 'Kilometres per hour (km/h)'
    },
    {
        value: OrganisationUnitsPreferences.SpeedUnit.FEET_PER_SECOND,
        name: 'Feet per second (fps)'
    },
    {
        value: OrganisationUnitsPreferences.SpeedUnit.MILES_PER_HOUR,
        name: 'Miles per hour (mph)'
    },
    { value: OrganisationUnitsPreferences.SpeedUnit.KNOTS, name: 'Knots (kt)' }
];

export const temperatureFormatOptions: LabelValue[] = [
    {
        value: OrganisationUnitsPreferences.TemperatureUnit.CELSIUS,
        name: 'Celsius (c)'
    },
    {
        value: OrganisationUnitsPreferences.TemperatureUnit.FAHRENHEIT,
        name: 'Fahrenheit (f)'
    }
];

export const weightFormatOptions: LabelValue[] = [
    { value: OrganisationUnitsPreferences.WeightUnit.GRAM, name: 'Grams (g)' },
    {
        value: OrganisationUnitsPreferences.WeightUnit.POUND,
        name: 'Pounds (lb)'
    }
];

/* ==== Example values for non-realtime data ==== */

export const distanceExamples: LabelValue[] = [
    {
        value: OrganisationUnitsPreferences.DistanceUnit.METRE,
        name: '1000 m'
    },
    {
        value: OrganisationUnitsPreferences.DistanceUnit.MILE,
        name: '1 mi'
    },
    {
        value: OrganisationUnitsPreferences.DistanceUnit.NAUTICAL_MILE,
        name: '1 nm'
    }
];

export const heightExamples: LabelValue[] = [
    {
        value: OrganisationUnitsPreferences.HeightUnit.METRE,
        name: '40 m'
    },
    { value: OrganisationUnitsPreferences.HeightUnit.FEET, name: '120 ft' }
];

export const speedExamples: LabelValue[] = [
    {
        value: OrganisationUnitsPreferences.SpeedUnit.METRES_PER_SECOND,
        name: '10 m/s'
    },
    {
        value: OrganisationUnitsPreferences.SpeedUnit.KILOMETRES_PER_HOUR,
        name: '10 km/h'
    },
    {
        value: OrganisationUnitsPreferences.SpeedUnit.FEET_PER_SECOND,
        name: '10 fps'
    },
    {
        value: OrganisationUnitsPreferences.SpeedUnit.MILES_PER_HOUR,
        name: '10 mph'
    },
    { value: OrganisationUnitsPreferences.SpeedUnit.KNOTS, name: '10 kt' }
];

export const temperatureExamples: LabelValue[] = [
    {
        value: OrganisationUnitsPreferences.TemperatureUnit.CELSIUS,
        name: '30 c'
    },
    {
        value: OrganisationUnitsPreferences.TemperatureUnit.FAHRENHEIT,
        name: '86 f'
    }
];

export const weightExamples: LabelValue[] = [
    { value: OrganisationUnitsPreferences.WeightUnit.GRAM, name: '500 g' },
    {
        value: OrganisationUnitsPreferences.WeightUnit.POUND,
        name: '1 lb'
    }
];

/* ==== Formatter functions for labels and examples ==== */

export function formatCurrentMoment(format: string) {
    if (format == null) {
        return this.working ? 'Loading...' : '';
    }
    return moment().format(format);
}

export function findFormatLabelFromValue(
    options: LabelValue[],
    format: string
) {
    if (format == null) {
        return this.working ? 'Loading...' : '';
    }
    return options.find(opt => opt.value === format)?.name ?? format;
}

/* ==== Component Interfaces ==== */

export interface OrganisationFormSettings {
    dateFormat: FormControl<string>;
    dateTimeFormat: FormControl<string>;
    distanceUnit: FormControl<string>;
    heightUnit: FormControl<string>;
    speedUnit: FormControl<string>;
    temperatureUnit: FormControl<string>;
    timeFormat: FormControl<string>;
    weightUnit: FormControl<string>;
}
